import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { CheckCircle2, ChevronDown, ChevronRight, ShieldCheck } from 'lucide-react';
import { CHECK_STAGE_LABEL, type CheckStage } from '@hcc/types';
import { api, ApiError } from '../../lib/api';
import { Button } from '../ui/Button';
import { CardSection } from '../ui/Card';
import { Pill } from '../ui/Pill';
import { LinkifiedText } from '../ui/LinkifiedText';
import type { OnboardingTask } from './OnboardingTasks';

export const CHECK_STAGE_TONE: Record<
  CheckStage,
  'success' | 'warning' | 'danger' | 'info' | 'neutral'
> = {
  not_applied: 'neutral',
  applied: 'info',
  processed: 'warning',
  verified: 'success',
  changes_requested: 'danger',
};

/**
 * The body of a screening check (DBS / DVLA) on the dedicated
 * /me/tasks/:taskId page; the page renders the title, this renders the
 * flow. The task description carries the application instructions
 * (portal link, organisation reference / code); the inductee follows
 * them externally and ticks "I have applied". HR drives the later
 * stages from the admin checks page, so past 'applied' this is
 * informational.
 */
export function CheckTaskRow({ task }: { task: OnboardingTask }) {
  const qc = useQueryClient();
  const stage: CheckStage = task.check_stage ?? 'not_applied';
  const actionable = stage === 'not_applied' || stage === 'changes_requested';
  // Instructions stay one click away after applying, in case the
  // reference is needed again, but stop dominating the row.
  const [showInstructions, setShowInstructions] = useState(actionable);

  const apply = useMutation({
    mutationFn: () =>
      api<{ event: unknown }>(`/api/me/tasks/${task.task_id}/submit`, {
        method: 'POST',
        body: JSON.stringify({}),
      }),
    onSuccess: () => {
      toast.success('Thanks, HR will review your application');
      setShowInstructions(false);
      qc.invalidateQueries({ queryKey: ['me-view'] });
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : 'Could not update'),
  });

  return (
    <CardSection>
      <div className="flex flex-wrap items-center gap-2">
        <Pill
          tone={CHECK_STAGE_TONE[stage]}
          icon={
            stage === 'verified' ? (
              <CheckCircle2 className="h-3 w-3" />
            ) : (
              <ShieldCheck className="h-3 w-3" />
            )
          }
        >
          {CHECK_STAGE_LABEL[stage]}
        </Pill>
      </div>
      {stage === 'changes_requested' && task.rejection_reason ? (
        <p className="mt-2 text-sm text-warning-soft-fg">{task.rejection_reason}</p>
      ) : null}
      {stage === 'applied' ? (
        <p className="mt-2 text-sm text-fg-muted">
          HR is checking your application on the screening portal.
        </p>
      ) : null}
      {stage === 'processed' ? (
        <p className="mt-2 text-sm text-fg-muted">
          Your application has been processed. When your certificate arrives in the
          post, bring the ORIGINAL with you on your start day; scans or copies
          cannot be accepted.
        </p>
      ) : null}
      {stage === 'verified' ? (
        <p className="mt-2 text-sm text-fg-muted">
          All done; your certificate has been verified. Nothing more to do here.
        </p>
      ) : null}

      {task.description ? (
        <div className="mt-2">
          {!actionable ? (
            <button
              type="button"
              onClick={() => setShowInstructions((s) => !s)}
              className="inline-flex items-center gap-1 text-xs font-medium text-accent hover:underline"
            >
              {showInstructions ? (
                <ChevronDown className="h-3 w-3" />
              ) : (
                <ChevronRight className="h-3 w-3" />
              )}
              {showInstructions ? 'Hide instructions' : 'Show instructions'}
            </button>
          ) : null}
          {showInstructions ? (
            <div className="mt-2 rounded-token-sm bg-surface-2 px-3 py-2.5 text-sm text-fg">
              <LinkifiedText text={task.description} />
            </div>
          ) : null}
        </div>
      ) : null}

      {actionable ? (
        <div className="mt-3">
          <Button
            size="sm"
            variant="success"
            iconLeft={<CheckCircle2 className="h-4 w-4" />}
            loading={apply.isPending}
            onClick={() => apply.mutate()}
          >
            {stage === 'changes_requested' ? 'I have re-applied' : 'I have applied'}
          </Button>
        </div>
      ) : null}
    </CardSection>
  );
}
